import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Spinner } from "react-bootstrap";
import { Autocomplete, TextField } from "@mui/material";
import SubmitButton from "components/Buttons/SubmitButton";
import { EditButton } from "components/Buttons/EditButton";
import { DeleteButton } from "components/Buttons/DeleteButton";
import SaveCancelButtons from "components/Buttons/SaveCancelButtons";
import { Axios } from "api/axios";
import { fetchCategoryByDropDown } from "slices/category/thunk";
import { useDispatch } from "react-redux";
import { AppDispatch } from "slices/store";
import { fetchSubCategoryDropdownByCategoryId } from "slices/sub-category/thunk";
import { autocompleteDropdownStyles, tableStyles } from "Constants/Style";
import { toast } from "react-toastify";
import { InwardType } from "Constants/inwardTypes";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";

interface ItemOption {
  name: string;
  code: string;
  id: number;
  unit?: string;
  selling_price?: string | number;
}

interface CategoryOption {
  category_name: string;
  category_id?: string;
}

interface SubCategoryOption {
  sub_category_name: string;
  sub_category_id?: string;
}

interface FormData {
  category: string | null;
  category_id: string | null; // Category UUID
  subCategory: string | null;
  sub_category_id: string | null; // Sub Category UUID
  itemName: string | null;
  item_id: string | null; // Item UUID
  itemCode: string | null;
  unit: string | null;
  rate: number;
  amount: number;
  remarks: string;
  quantity: number;
  issue_quantity: number;
  received_quantity: number;
  order_quantity: number;
  return_quantity: number;
}

interface LoadingStates {
  items: boolean;
  categories: boolean;
  subcategories: boolean;
  units: boolean;
}

interface RowData {
  id: string;
  item_id?: string; // Item UUID
  master_item?: any;
  itemName: string;
  itemCode: string;
  category: string;
  category_id?: string; // Category UUID
  subCategory: string;
  sub_category_id?: string; // Sub Category UUID
  unit: string;
  rate: number;
  amount: number;
  remarks: string;
  quantity?: number;
  issue_quantity?: number;
  received_quantity?: number;
  order_quantity?: number;
  return_quantity?: number;
   inventory_item_id?: string; // For edit mode - existing inventory item ID
  item_type?: string; // For labour inward - 'new' or 'returned'
  bag_no?: string; // Bag number for existing items
  sr_no?: number; // Serial number
}

interface PropsTypes {
  inwardType: string;
  onItemsUpdated?: (items: RowData[]) => void;
  initialItems?: RowData[]; // For edit mode
}

const ConfigureItems = ({
  inwardType,
  onItemsUpdated,
  initialItems,
}: PropsTypes) => {
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState<
    SubCategoryOption[]
  >([]);
  const [itemOptions, setItemOptions] = useState<ItemOption[]>([]);
  const [rows, setRows] = useState<RowData[]>([]);

  // Initialize rows with initialItems if provided (for edit mode)
  useEffect(() => {
    // console.log("Configure Items",initialItems)
    if (initialItems && initialItems.length > 0 && rows.length === 0) {
      // Calculate amount for each item from rate and quantity
      const itemsWithAmount = initialItems.map((item) => {
        const rate = parseFloat(String(item.rate)) || 0;
        const quantity = parseFloat(String(item.quantity)) || 0;
        const amount = rate * quantity;
        
        return {
          ...item,
          amount: amount,
        };
      });
      
      setRows(itemsWithAmount);
      // Notify parent about initial items with calculated amounts
      if (onItemsUpdated) {
        onItemsUpdated(itemsWithAmount);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialItems]);

  useEffect(()=>{
    console.log("Initial item from configure component",initialItems)
  },[initialItems])

  const [openCategory, setOpenCategory] = useState(false);
  const [openSubCategory, setOpenSubCategory] = useState(false);
  const [openItemName, setOpenItemName] = useState(false);

  const dispatch = useDispatch<AppDispatch>();

  const [formData, setFormData] = useState<FormData>({
    category: null,
    category_id: null,
    subCategory: null,
    sub_category_id: null,
    itemName: null,
    item_id: null,
    itemCode: null,
    unit: null,
    rate: 0,
    amount: 0,
    remarks: "",
    quantity: 0,
    issue_quantity: 0,
    received_quantity: 0,
    order_quantity: 0,
    return_quantity: 0,
  });

  const [loading, setLoading] = useState<LoadingStates>({
    categories: false,
    subcategories: false,
    items: false,
    units: false,
  });

  const [adding, setAdding] = useState(false);
  const [deletingRows, setDeletingRows] = useState<string[]>([]);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editingRowData, setEditingRowData] = useState<RowData | null>(null);
  const [formErrors, setFormErrors] = useState<{
    rate?: string;
    quantity?: string;
    issue_quantity?: string;
    received_quantity?: string;
    order_quantity?: string;
    return_quantity?: string;
  }>({});
  const [editErrors, setEditErrors] = useState<{
    rate?: string;
    quantity?: string;
    issue_quantity?: string;
    received_quantity?: string;
    order_quantity?: string;
    return_quantity?: string;
  }>({});

  const fetchCategories = async (): Promise<void> => {
    setLoading((prev) => ({ ...prev, categories: true }));
    try {
      const res = await dispatch(fetchCategoryByDropDown()).unwrap();
      setCategoryOptions(res.data || []);
    } catch {
      console.error("Failed to fetch categories");
    } finally {
      setLoading((prev) => ({ ...prev, categories: false }));
    }
  };

  const fetchSubcategories = async (categoryId: string): Promise<void> => {
    setLoading((l) => ({ ...l, subcategories: true }));
    try {
      // const res = await Axios.get(
      //   `master/sub-category/dropdown/category/${categoryId}`
      // );

      const res = await dispatch(
        fetchSubCategoryDropdownByCategoryId(categoryId)
      ).unwrap();
      console.log("Subcategories Response:", res);
      setSubCategoryOptions(
        res?.data.map((s: any) => ({
          sub_category_name: s.sub_category_name,
          sub_category_id: s.sub_category_id,
        }))
      );
    } catch {
      setSubCategoryOptions([]);
    } finally {
      setLoading((l) => ({ ...l, subcategories: false }));
    }
  };

  const fetchItems = async (categoryId: string, subCategoryId: string) => {
    setLoading((prev) => ({ ...prev, items: true }));

    try {
      const res = await Axios.get(
        `/master/item/dropdown/category/${categoryId}/sub-category/${subCategoryId}`
      );

      // Backend API not found, using thunk instead
      // const res1 = await dispatch(fetchItemsBySubCategoryId(subCategoryId)).unwrap();
      // console.log("Items Response:", res);

      const list = res.data?.data || [];

      const items = list.map((item: any, idx: number) => ({
        name: item.item_name,
        code: item.item_code,
        id: item.item_id || idx,
        unit: item?.unit?.unit_name,
        selling_price: item.selling_price || 0,
      }));

      setItemOptions(items);
    } catch {
      setItemOptions([]);
    } finally {
      setLoading((prev) => ({ ...prev, items: false }));
    }
  };

  const handleAutocompleteChange = (
    key: keyof FormData,
    value: ItemOption | CategoryOption | SubCategoryOption | null
  ): void => {
    let updatedFormData = { ...formData };

    if (key === "category") {
      if (!value) {
        updatedFormData = {
          ...updatedFormData,
          category: null,
          category_id: null,
          subCategory: null,
          sub_category_id: null,
          itemName: null,
          item_id: null,
          itemCode: null,
          unit: null,
        };
        setSubCategoryOptions([]);
        setItemOptions([]);
      } else {
        const cat = value as CategoryOption;
        updatedFormData.category = cat.category_name;
        updatedFormData.category_id = cat.category_id || null;
        updatedFormData.subCategory = null;
        updatedFormData.sub_category_id = null;
        setItemOptions([]);
      }
    }

    if (key === "subCategory") {
      if (!value) {
        updatedFormData = {
          ...updatedFormData,
          subCategory: null,
          sub_category_id: null,
          itemName: null,
          item_id: null,
          itemCode: null,
          unit: null,
        };
        setItemOptions([]);
      } else {
        const sub = value as SubCategoryOption;
        updatedFormData = {
          ...updatedFormData,
          itemName: null,
          item_id: null,
          itemCode: null,
          unit: null,
        };
        updatedFormData.subCategory = sub.sub_category_name;
        updatedFormData.sub_category_id = sub.sub_category_id || null;
      }
    }

    if (key === "itemName") {
      if (!value) {
        updatedFormData.itemName = null;
        updatedFormData.item_id = null;
        updatedFormData.itemCode = null;
        updatedFormData.unit = null;
        updatedFormData.rate = 0;
        updatedFormData.amount = 0;
      } else {
        const item = value as ItemOption;
        updatedFormData.itemName = item.name;
        updatedFormData.item_id = String(item.id) || null;
        updatedFormData.itemCode = item.code;
        updatedFormData.unit = item.unit || null;
        // Autofill rate from item's selling_price
        const sellingPrice = parseFloat(String(item.selling_price || 0));
        updatedFormData.rate = sellingPrice;
        // Recalculate amount if quantity is already set
        const quantity = parseFloat(String(updatedFormData.quantity)) || 0;
        updatedFormData.amount = sellingPrice * quantity;
      }
    }

    setFormData(updatedFormData);
  };

  const handleChange = (key: keyof FormData, value: string | number): void => {
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    
    // Validate negative values
    if (
      (key === "rate" || 
       key === "quantity" || 
       key === "issue_quantity" || 
       key === "received_quantity" || 
       key === "order_quantity" || 
       key === "return_quantity") &&
      !isNaN(numValue) &&
      numValue < 0
    ) {
      const errorMessage = `${key === "rate" ? "Rate" : 
        key === "quantity" ? "Quantity" :
        key === "issue_quantity" ? "Issue Quantity" :
        key === "received_quantity" ? "Received Quantity" :
        key === "order_quantity" ? "Order Quantity" :
        "Return Quantity"} cannot be negative`;
      
      setFormErrors((prev) => ({ ...prev, [key]: errorMessage }));
      toast.error(errorMessage);
      return; // Don't update the value if it's negative
    }

    // Clear error if value is valid
    if (formErrors[key as keyof typeof formErrors]) {
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated[key as keyof typeof formErrors];
        return updated;
      });
    }

    setFormData((prev) => {
      const updated = { ...prev, [key]: value };

      if (key === "rate" || key === "quantity") {
        const rate = parseFloat(String(updated.rate)) || 0;
        const quantity = parseFloat(String(updated.quantity)) || 0;
        updated.amount = rate * quantity;
      }

      return updated;
    });
  };

  const handleAdd = (): void => {
    if (!formData.itemName || !formData.category || !formData.subCategory) {
      toast.warning(
        "Please select all required fields (Category, Sub Category, and Item Name)"
      );
      return;
    }

    // For gnrdirect type, validate quantity and rate
    if (inwardType === InwardType.SUPPLIER_INWARD) {
      if (!formData.quantity || formData.quantity <= 0) {
        toast.warning("Quantity is required and must be greater than 0");
        return;
      }
      if (!formData.rate || formData.rate <= 0) {
        toast.warning("Rate is required and must be greater than 0");
        return;
      }
    }

    // For labour type, validate received_quantity
    if (inwardType === InwardType.LABOUR_INWARD) {
      if (!formData.received_quantity || formData.received_quantity <= 0) {
        toast.warning(
          "Received Quantity is required and must be greater than 0"
        );
        return;
      }
    }

    // For salesOrderReturn type, validate order_quantity, return_quantity, and rate
    if (inwardType === InwardType.ORDER_RETURN) {
      if (!formData.order_quantity || formData.order_quantity <= 0) {
        toast.warning("Order Quantity is required and must be greater than 0");
        return;
      }
      if (!formData.return_quantity || formData.return_quantity <= 0) {
        toast.warning("Return Quantity is required and must be greater than 0");
        return;
      }
      if (!formData.rate || formData.rate <= 0) {
        toast.warning("Rate is required and must be greater than 0");
        return;
      }
    }

    setAdding(true);

    setTimeout(() => {
      const newRow: RowData = {
        id: Date.now().toString(),
        item_id: formData.item_id || undefined,
        itemName: formData.itemName!,
        itemCode: formData.itemCode || "",
        category: formData.category!,
        category_id: formData.category_id || undefined,
        subCategory: formData.subCategory!,
        sub_category_id: formData.sub_category_id || undefined,
        unit: formData.unit || "",
        rate: formData.rate,
        amount: formData.amount,
        remarks: formData.remarks,
      };

      if (inwardType === InwardType.SUPPLIER_INWARD) {
        newRow.quantity = formData.quantity;
      }

      if (inwardType === InwardType.LABOUR_INWARD) {
        newRow.received_quantity = formData.received_quantity;
         newRow.item_type = 'new'; // New items in labour inward
      }

      if (inwardType === InwardType.ORDER_RETURN) {
        newRow.order_quantity = formData.order_quantity;
        newRow.return_quantity = formData.return_quantity;
      }

      // setRows((prev) => [...prev, newRow]);
      setRows((prev) => {
        const updatedRows = [...prev, newRow];
        if (onItemsUpdated) {
          onItemsUpdated(updatedRows);
        }
        return updatedRows;
      });
      // onItemsUpdated(rows);

      setFormData({
        category: null,
        category_id: null,
        subCategory: null,
        sub_category_id: null,
        itemName: null,
        item_id: null,
        itemCode: null,
        unit: null,
        rate: 0,
        amount: 0,
        remarks: "",
        quantity: 0,
        issue_quantity: 0,
        received_quantity: 0,
        order_quantity: 0,
        return_quantity: 0,
      });

      setSubCategoryOptions([]);
      setItemOptions([]);
      setFormErrors({}); // Clear errors when adding item

      setAdding(false);
    }, 400);
  };

  // const handleDelete = (id: string): void => {
  //   setDeletingRows((prev) => [...prev, id]);
  //   setTimeout(() => {
  //     setRows((prev) => prev.filter((r) => r.id !== id));
  //     setDeletingRows((prev) => prev.filter((x) => x !== id));
  //   }, 300);
  // };

  const handleDelete = (id: string): void => {
    // Trigger animation
    setDeletingRows((prev) => [...prev, id]);

    setTimeout(() => {
      setRows((prev) => {
        const updated = prev.filter((r) => r.id !== id);

        // 🔥 Push updated rows up to parent
        if (updated && onItemsUpdated) {
          onItemsUpdated(updated);
        }

        return updated;
      });

      // Clear animation state
      setDeletingRows((prev) => prev.filter((x) => x !== id));
      
      // Show success toast
      toast.success("Item deleted successfully");
    }, 300);
  };

  const handleEdit = async (row: RowData): Promise<void> => {
    setEditingRowId(row.id);
    setEditingRowData({ ...row });
    setEditErrors({}); // Clear any previous errors when starting to edit

    // Load subcategories if category_id exists
    if (row.category_id) {
      await fetchSubcategories(row.category_id);
    }

    // Load items if category_id and sub_category_id exist
    if (row.category_id && row.sub_category_id) {
      await fetchItems(row.category_id, row.sub_category_id);
    }
  };

  const handleSaveEdit = (): void => {
    if (!editingRowData) return;

    // Validate rate for inward types that require rate (not LABOUR_INWARD)
    if (inwardType !== InwardType.LABOUR_INWARD) {
      if (
        !editingRowData.rate ||
        editingRowData.rate === undefined ||
        editingRowData.rate === null ||
        editingRowData.rate <= 0
      ) {
        toast.warning("Rate is required and must be greater than 0");
        setEditErrors((prev) => ({
          ...prev,
          rate: "Rate is required and must be greater than 0",
        }));
        return;
      }
    }

    // Validate quantity for SUPPLIER_INWARD
    if (inwardType === InwardType.SUPPLIER_INWARD) {
      if (
        !editingRowData.quantity ||
        editingRowData.quantity === undefined ||
        editingRowData.quantity === null ||
        editingRowData.quantity <= 0
      ) {
        toast.warning("Quantity is required and must be greater than 0");
        setEditErrors((prev) => ({
          ...prev,
          quantity: "Quantity is required and must be greater than 0",
        }));
        return;
      }
    }

    setRows((prev) => {
      const updated = prev.map((r) =>
        r.id === editingRowId ? editingRowData : r
      );

      // 🔥 Push updated rows up to parent
      if (onItemsUpdated) {
        onItemsUpdated(updated);
      }

      return updated;
    });

    setEditingRowId(null);
    setEditingRowData(null);
    setEditErrors({}); // Clear errors when saving
    
    // Show success toast
    toast.success("Item updated successfully");
  };

  const handleCancelEdit = (): void => {
    setEditingRowId(null);
    setEditingRowData(null);
    setEditErrors({}); // Clear errors when canceling edit
    toast.warning("Item update cancelled");
  };

  const handleEditFieldChange = (
    key: keyof RowData,
    value: string | number
  ): void => {
    if (!editingRowData) return;

    const numValue = typeof value === "string" ? parseFloat(value) : value;
    
    // Validate negative values
    if (
      (key === "rate" || 
       key === "quantity" || 
       key === "issue_quantity" || 
       key === "received_quantity" || 
       key === "order_quantity" || 
       key === "return_quantity") &&
      !isNaN(numValue) &&
      numValue < 0
    ) {
      const errorMessage = `${key === "rate" ? "Rate" : 
        key === "quantity" ? "Quantity" :
        key === "issue_quantity" ? "Issue Quantity" :
        key === "received_quantity" ? "Received Quantity" :
        key === "order_quantity" ? "Order Quantity" :
        "Return Quantity"} cannot be negative`;
      
      setEditErrors((prev) => ({ ...prev, [key]: errorMessage }));
      toast.error(errorMessage);
      return; // Don't update the value if it's negative
    }

    // Clear error if value is valid
    if (editErrors[key as keyof typeof editErrors]) {
      setEditErrors((prev) => {
        const updated = { ...prev };
        delete updated[key as keyof typeof editErrors];
        return updated;
      });
    }

    const updated = { ...editingRowData, [key]: value };

    // Recalculate amount if rate or quantity changes
    if (key === "rate" || key === "quantity") {
      const rate = parseFloat(String(updated.rate)) || 0;
      const quantity = parseFloat(String(updated.quantity)) || 0;
      updated.amount = rate * quantity;
    }

    setEditingRowData(updated);
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container fluid className="mx-auto mt-4">
      <Row className="align-items-center mb-4">
        <Col>
          <h6 className="card-title mb-0">Items Details</h6>
        </Col>
      </Row>

      <style>
        {`
          .sticky-column {
            position: relative;
          }
          .sticky-column::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 1px;
            background-color: #dee2e6;
            z-index: 12;
          }
          thead .sticky-column::before {
            z-index: 13;
          }
        `}
      </style>


      <Table hover responsive className="align-middle text-center">
        <thead>
          <tr>
            <th style={{ ...tableStyles.headerAlt, minWidth: "80px" }}>
              Sr No.
            </th>
            <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
              Category
            </th>
            <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
              Sub Category
            </th>
            <th style={{ ...tableStyles.headerAlt, minWidth: "200px" }}>
              Item Name
            </th>
            <th style={{ ...tableStyles.headerAlt, minWidth: "150px" }}>
              Item Code
            </th>
            {inwardType === InwardType.SUPPLIER_INWARD && (
              <th style={{ ...tableStyles.headerAlt, minWidth: "120px" }}>
                Quantity
              </th>
            )}

            {inwardType === InwardType.LABOUR_INWARD && (
              <th style={{ ...tableStyles.headerAlt, minWidth: "180px" }}>
                Received Quantity
              </th>
            )}

            {inwardType === InwardType.ORDER_RETURN && (
              <>
                <th style={{ ...tableStyles.headerAlt, minWidth: "120px" }}>
                  Order Quantity
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "120px" }}>
                  Return Quantity
                </th>
              </>
            )}

            <th style={{ ...tableStyles.headerAlt, minWidth: "100px" }}>
              Unit
            </th>

            {inwardType !== InwardType.LABOUR_INWARD && (
              <>
                <th style={{ ...tableStyles.headerAlt, minWidth: "120px" }}>
                  Rate
                </th>
                <th style={{ ...tableStyles.headerAlt, minWidth: "120px" }}>
                  Amount
                </th>
              </>
            )}

            <th
              style={{
                ...tableStyles.headerAlt,
                minWidth: "200px",
                maxWidth: "250px",
              }}
            >
              Remarks
            </th>

            <th
              className="sticky-column"
              style={{
                ...tableStyles.headerAlt,
                ...tableStyles.actionHeader,
                minWidth: "100px",
              }}
            >
              Action
            </th>
          </tr>
        </thead>

        <tbody>
          {/* Add Row */}
          <tr>
            <td>Select Item</td>

            {/* Category */}
            <td>
              <Autocomplete
                open={openCategory}
                onOpen={() => setOpenCategory(true)}
                onClose={() => setOpenCategory(false)}
                size="small"
                options={categoryOptions}
                getOptionLabel={(opt) => opt.category_name || ""}
                value={
                  categoryOptions.find(
                    (c) => c.category_name === formData.category
                  ) || null
                }
                loading={loading.categories}
                onChange={(_, value) => {
                  handleAutocompleteChange("category", value);
                  if (value?.category_id) fetchSubcategories(value.category_id);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Category"
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                )}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.category_name || ""
                  )
                }
                ListboxProps={{
                  ...autocompleteDropdownStyles.ListboxProps,
                  style: {
                    maxHeight: "200px",
                    overflow: "auto",
                    zIndex: 100,
                  },
                }}
                componentsProps={{
                  popper: {
                    style: {
                      zIndex: 100,
                    },
                    modifiers: [
                      {
                        name: "preventOverflow",
                        enabled: true,
                      },
                    ],
                  },
                }}
              />
            </td>

            {/* Sub Category */}
            <td>
              <Autocomplete
                open={openSubCategory}
                onOpen={() => setOpenSubCategory(true)}
                onClose={() => setOpenSubCategory(false)}
                size="small"
                options={subCategoryOptions}
                getOptionLabel={(opt) => opt.sub_category_name || ""}
                value={
                  subCategoryOptions.find(
                    (s) => s.sub_category_name === formData.subCategory
                  ) || null
                }
                disabled={!formData.category}
                loading={loading.subcategories}
                onChange={(_, value) => {
                  handleAutocompleteChange("subCategory", value);

                  if (value?.sub_category_id && formData.category) {
                    const selectedCat = categoryOptions.find(
                      (c) => c.category_name === formData.category
                    );
                    if (selectedCat?.category_id) {
                      fetchItems(
                        selectedCat.category_id,
                        value.sub_category_id
                      );
                    }
                  }

                  if (!value) setItemOptions([]);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      !formData.category
                        ? "Select Category First"
                        : "Select Sub Category"
                    }
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                )}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.sub_category_name || ""
                  )
                }
                ListboxProps={{
                  ...autocompleteDropdownStyles.ListboxProps,
                  style: {
                    maxHeight: "200px",
                    overflow: "auto",
                    zIndex: 100,
                  },
                }}
                componentsProps={{
                  popper: {
                    style: {
                      zIndex: 100,
                    },
                    modifiers: [
                      {
                        name: "preventOverflow",
                        enabled: true,
                      },
                    ],
                  },
                }}
              />
            </td>

            {/* Item Name */}
            <td>
              <Autocomplete
                open={openItemName}
                onOpen={() => setOpenItemName(true)}
                onClose={() => setOpenItemName(false)}
                size="small"
                options={itemOptions}
                getOptionLabel={(opt) => opt.name || ""}
                value={
                  itemOptions.find((o) => o.name === formData.itemName) || null
                }
                disabled={!formData.subCategory}
                loading={loading.items}
                onChange={(_, value) =>
                  handleAutocompleteChange("itemName", value)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Item"
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                )}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) => opt.name || ""
                  )
                }
                ListboxProps={{
                  ...autocompleteDropdownStyles.ListboxProps,
                  style: {
                    maxHeight: "200px",
                    overflow: "auto",
                    zIndex: 100,
                  },
                }}
                componentsProps={{
                  popper: {
                    style: {
                      zIndex: 100,
                    },
                    modifiers: [
                      {
                        name: "preventOverflow",
                        enabled: true,
                      },
                    ],
                  },
                }}
              />
            </td>

            {/* Item Code */}
            <td>
              <TextField
                size="small"
                value={formData.itemCode || ""}
                disabled
                placeholder="Code"
                sx={{
                  ...autocompleteDropdownStyles.inputSx,
                  "& .MuiInputBase-root": {
                    height: "32px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "6px 12px",
                    height: "20px",
                  },
                }}
              />
            </td>

            {/* Quantity */}
            {inwardType === InwardType.SUPPLIER_INWARD && (
              <td>
                <TextField
                  size="small"
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => handleChange("quantity", e.target.value)}
                  inputProps={{ min: 0 }}
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiInputBase-root": {
                      height: "32px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 12px",
                      height: "20px",
                    },
                  }}
                />
              </td>
            )}

            {/* Received Quantity */}
            {inwardType === InwardType.LABOUR_INWARD && (
              <td>
                <TextField
                  size="small"
                  type="number"
                  value={formData.received_quantity}
                  onChange={(e) =>
                    handleChange("received_quantity", e.target.value)
                  }
                  inputProps={{ min: 0 }}
                  sx={{
                    ...autocompleteDropdownStyles.inputSx,
                    "& .MuiInputBase-root": {
                      height: "32px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 12px",
                      height: "20px",
                    },
                  }}
                />
              </td>
            )}

            {/* Order Quantity & Return Quantity */}
            {inwardType === InwardType.ORDER_RETURN && (
              <>
                <td>
                  <TextField
                    size="small"
                    type="number"
                    value={formData.order_quantity}
                    onChange={(e) =>
                      handleChange("order_quantity", e.target.value)
                    }
                    inputProps={{ min: 0 }}
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                </td>
                <td>
                  <TextField
                    size="small"
                    type="number"
                    value={formData.return_quantity}
                    onChange={(e) =>
                      handleChange("return_quantity", e.target.value)
                    }
                    inputProps={{ min: 0 }}
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                </td>
              </>
            )}

            {/* Unit */}
            <td>
              <TextField
                size="small"
                value={formData.unit || ""}
                disabled
                placeholder="Unit"
                sx={{
                  ...autocompleteDropdownStyles.inputSx,
                  "& .MuiInputBase-root": {
                    height: "32px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "6px 12px",
                    height: "20px",
                  },
                }}
              />
            </td>

            {/* Rate & Amount */}
            {inwardType !== InwardType.LABOUR_INWARD && (
              <>
                <td>
                  <TextField
                    size="small"
                    type="number"
                    value={formData.rate}
                    onChange={(e) => handleChange("rate", e.target.value)}
                    inputProps={{ min: 0 }}
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                </td>
                <td>
                  <TextField
                    size="small"
                    value={formData.amount}
                    disabled
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                      "& .MuiInputBase-root": {
                        height: "32px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "6px 12px",
                        height: "20px",
                      },
                    }}
                  />
                </td>
              </>
            )}

            {/* Remarks */}
            <td
              style={{
                maxWidth: "250px",
                whiteSpace: "normal",
                wordWrap: "break-word",
              }}
            >
              <TextField
                size="small"
                value={formData.remarks}
                onChange={(e) => handleChange("remarks", e.target.value)}
                sx={{
                  ...autocompleteDropdownStyles.inputSx,
                  "& .MuiInputBase-root": {
                    height: "32px",
                  },
                  "& .MuiInputBase-input": {
                    padding: "6px 12px",
                    height: "20px",
                  },
                }}
              />
            </td>

            {/* Action Button */}
            <td className="sticky-column" style={tableStyles.actionCell}>
              <div style={tableStyles.actionButtonContainer}>
                <SubmitButton isSubmitting={adding} onClick={handleAdd} />
              </div>
            </td>
          </tr>

          {/* Existing Rows */}
          {rows.map((row, idx) => {
            const isEditing = editingRowId === row.id;
            const displayData =
              isEditing && editingRowData ? editingRowData : row;

            return (
              <tr key={row.id} style={isEditing ? tableStyles.rowEditing : {}}>
                <td style={tableStyles.cell}>{idx + 1}</td>

                {/* Category */}
                <td style={tableStyles.cell}>
                  <span>{displayData.category || ""}</span>
                </td>

                {/* Sub Category */}
                <td style={tableStyles.cell}>
                  <span>{displayData.subCategory || ""}</span>
                </td>

                {/* Item Name */}
                <td style={tableStyles.cell}>
                  <span>{displayData.itemName || ""}</span>
                </td>

                {/* Item Code */}
                <td style={tableStyles.cell}>
                  <span>{displayData.itemCode || ""}</span>
                </td>

                {/* Quantity (Supplier Inward) */}
                {inwardType === InwardType.SUPPLIER_INWARD && (
                  <td style={tableStyles.cell}>
                    {isEditing ? (
                      <TextField
                        size="small"
                        type="number"
                        value={displayData.quantity || 0}
                        onChange={(e) =>
                          handleEditFieldChange(
                            "quantity",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        inputProps={{ min: 0 }}
                        fullWidth
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                          "& .MuiInputBase-input": {
                            padding: "6px 12px",
                            height: "20px",
                          },
                        }}
                      />
                    ) : (
                      <span>{row.quantity || 0}</span>
                    )}
                  </td>
                )}

                {/* Received Quantity (Labour Inward) */}
                {inwardType === InwardType.LABOUR_INWARD && (
                  <td style={tableStyles.cell}>
                    {isEditing ? (
                      <TextField
                        size="small"
                        type="number"
                        value={displayData.received_quantity || 0}
                        onChange={(e) =>
                          handleEditFieldChange(
                            "received_quantity",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        inputProps={{ min: 0 }}
                        fullWidth
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                          "& .MuiInputBase-root": {
                            height: "32px",
                          },
                          "& .MuiInputBase-input": {
                            padding: "6px 12px",
                            height: "20px",
                          },
                        }}
                      />
                    ) : (
                      <span>{row.received_quantity || 0}</span>
                    )}
                  </td>
                )}

                {/* Order/Return Quantity (Order Return) */}
                {inwardType === InwardType.ORDER_RETURN && (
                  <>
                    <td style={tableStyles.cell}>
                      {isEditing ? (
                        <TextField
                          size="small"
                          type="number"
                          value={displayData.order_quantity || 0}
                          onChange={(e) =>
                            handleEditFieldChange(
                              "order_quantity",
                              parseFloat(e.target.value) || 0
                            )
                          }
                          inputProps={{ min: 0 }}
                          fullWidth
                          sx={{
                            ...autocompleteDropdownStyles.inputSx,
                            "& .MuiInputBase-root": {
                              height: "32px",
                            },
                            "& .MuiInputBase-input": {
                              padding: "6px 12px",
                              height: "20px",
                            },
                          }}
                        />
                      ) : (
                        <span>{row.order_quantity || 0}</span>
                      )}
                    </td>
                    <td style={tableStyles.cell}>
                      {isEditing ? (
                        <TextField
                          size="small"
                          type="number"
                          value={displayData.return_quantity || 0}
                          onChange={(e) =>
                            handleEditFieldChange(
                              "return_quantity",
                              parseFloat(e.target.value) || 0
                            )
                          }
                          inputProps={{ min: 0 }}
                          fullWidth
                          sx={{
                            ...autocompleteDropdownStyles.inputSx,
                            "& .MuiInputBase-root": {
                              height: "32px",
                            },
                            "& .MuiInputBase-input": {
                              padding: "6px 12px",
                              height: "20px",
                            },
                          }}
                        />
                      ) : (
                        <span>{row.return_quantity || 0}</span>
                      )}
                    </td>
                  </>
                )}

                {/* Unit */}
                <td style={tableStyles.cell}>
                  <span>{displayData.unit || ""}</span>
                </td>

                {/* Rate */}
                {inwardType !== InwardType.LABOUR_INWARD && (
                  <>
                    <td style={tableStyles.cell}>
                      {isEditing ? (
                        <TextField
                          size="small"
                          type="number"
                          value={displayData.rate || 0}
                          onChange={(e) =>
                            handleEditFieldChange(
                              "rate",
                              parseFloat(e.target.value) || 0
                            )
                          }
                          inputProps={{ min: 0 }}
                          fullWidth
                          sx={{
                            ...autocompleteDropdownStyles.inputSx,
                            "& .MuiInputBase-root": {
                              height: "32px",
                            },
                            "& .MuiInputBase-input": {
                              padding: "6px 12px",
                              height: "20px",
                            },
                          }}
                        />
                      ) : (
                        <span>{row.rate || 0}</span>
                      )}
                    </td>
                    <td style={tableStyles.cell}>
                      <span>{displayData.amount || 0}</span>
                    </td>
                  </>
                )}

                {/* Remarks */}
                <td
                  style={{
                    ...tableStyles.cell,
                    maxWidth: "250px",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                  }}
                >
                  {isEditing ? (
                    <TextField
                      size="small"
                      value={displayData.remarks || ""}
                      onChange={(e) =>
                        handleEditFieldChange("remarks", e.target.value)
                      }
                      fullWidth
                      sx={{
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiInputBase-root": {
                          height: "32px",
                        },
                        "& .MuiInputBase-input": {
                          padding: "6px 12px",
                          height: "20px",
                        },
                      }}
                    />
                  ) : (
                    <TruncatedTextCell
                      text={row.remarks}
                      maxWidth="300px"
                      maxLines={4}
                      rowIndex={idx}
                      fallbackText="—"
                    />
                  )}
                </td>

                {/* Actions */}
                <td
                  style={
                    isEditing
                      ? tableStyles.actionCellEditing
                      : tableStyles.actionCell
                  }
                  className="sticky-column"
                >
                  {isEditing ? (
                    <SaveCancelButtons
                      onSave={handleSaveEdit}
                      onCancel={handleCancelEdit}
                      containerStyle={tableStyles.actionButtonContainer}
                    />
                  ) : (
                    <div style={tableStyles.actionButtonContainer}>
                      <EditButton
                        onClick={() => handleEdit(row)}
                        iconClassName="ph-pencil"
                      />
                      <DeleteButton
                        onClick={() => handleDelete(row.id)}
                        iconClassName="ph-trash"
                        isLoading={deletingRows.includes(row.id)}
                      >
                        {deletingRows.includes(row.id) && (
                          <Spinner animation="border" size="sm" />
                        )}
                      </DeleteButton>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Container>
  );
};

export default ConfigureItems;
